import React, { useState, useCallback } from 'react';
import { Undo2, Redo2, FileText } from 'lucide-react';
import { Button } from './Button';
import styles from './CodeChangePills.module.css';

export type ChangeType = 'added' | 'modified' | 'removed';

export interface ChangeFile {
  filename: string;
  code: string;
  changeType: ChangeType;
  added?: number;
  removed?: number;
}

interface CodeChangePillsProps {
  files: ChangeFile[];
  onOpenFile?: (filename: string) => void;
  onViewDiff?: (filename: string) => void;
  onRevert?: (filename: string) => void;
  onRedo?: (filename: string) => void;
}

export const CodeChangePills: React.FC<CodeChangePillsProps> = ({
  files,
  onOpenFile,
  onViewDiff,
  onRevert,
  onRedo,
}) => {
  // Track which files have been reverted (to show redo arrow)
  const [revertedFiles, setRevertedFiles] = useState<Set<string>>(new Set());

  const handleRevert = useCallback(
    (filename: string) => {
      if (!confirm(`Revert ${filename} from checkpoint? This will overwrite the current file.`))
        return;
      onRevert?.(filename);
      setRevertedFiles((prev) => new Set(prev).add(filename));
    },
    [onRevert],
  );

  const handleRedo = useCallback(
    (filename: string) => {
      if (!confirm(`Re-apply AI changes to ${filename}? This will overwrite the current file.`))
        return;
      onRedo?.(filename);
      setRevertedFiles((prev) => {
        const next = new Set(prev);
        next.delete(filename);
        return next;
      });
    },
    [onRedo],
  );

  if (!files || files.length === 0) return null;

  return (
    <div className={styles.pillsWrapper}>
      {files.map((f, idx) => {
        const isReverted = revertedFiles.has(f.filename);
        return (
          <div key={idx} className={styles.pill} data-change-type={f.changeType}>
            <div className={styles.meta}>
              <span className={`${styles.statusBadge} ${styles[f.changeType]}`}>
                {f.changeType === 'added' ? '+' : f.changeType === 'modified' ? 'M' : '-'}
              </span>
              <span
                className={`${styles.filename} ${onOpenFile ? styles.clickable : ''}`}
                onClick={onOpenFile ? () => onOpenFile(f.filename) : undefined}
                title={onOpenFile ? `Open ${f.filename}` : undefined}
              >
                {f.filename}
              </span>
            </div>
            <div className={styles.statsRow}>
              <span className={styles.size}>{f.code.split('\n').length} lines</span>
              {(f.added !== undefined || f.removed !== undefined) && (
                <span className={styles.delta}>
                  {f.added ? `+${f.added}` : ''}
                  {f.removed ? ` -${f.removed}` : ''}
                </span>
              )}
            </div>

            <div className={styles.actions}>
              {onViewDiff && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onViewDiff(f.filename)}
                  title="View diff"
                >
                  <FileText size={14} />
                </Button>
              )}

              {/* Undo arrow: reverts file to checkpoint */}
              {onRevert && !isReverted && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleRevert(f.filename)}
                  title="Undo — revert file to checkpoint"
                >
                  <Undo2 size={14} />
                </Button>
              )}

              {/* Redo arrow: re-applies AI changes after revert */}
              {onRedo && isReverted && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleRedo(f.filename)}
                  title="Redo — re-apply AI changes"
                >
                  <Redo2 size={14} />
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
